import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import {
  FolderOpen, Upload, Search, Filter, Grid3X3, List, Image, FileText,
  Video, Music, File, Trash2, Download, Copy, X, Eye, MoreHorizontal,
  Plus, Tag, Clock, User, HardDrive, CheckCircle, AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Asset type icons
const assetTypeIcons = {
  image: { icon: Image, color: 'text-blue-500 bg-blue-50 dark:bg-blue-900/20' },
  video: { icon: Video, color: 'text-purple-500 bg-purple-50 dark:bg-purple-900/20' },
  document: { icon: FileText, color: 'text-orange-500 bg-orange-50 dark:bg-orange-900/20' },
  audio: { icon: Music, color: 'text-green-500 bg-green-50 dark:bg-green-900/20' },
  other: { icon: File, color: 'text-slate-500 bg-slate-50 dark:bg-slate-700' }
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

// Asset Detail Modal
const AssetDetailModal = ({ asset, open, onClose, onDelete, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: asset?.name || '',
    description: asset?.description || '',
    tags: asset?.tags?.join(', ') || ''
  });

  useEffect(() => {
    if (asset) {
      setFormData({
        name: asset.name || '',
        description: asset.description || '',
        tags: asset.tags?.join(', ') || ''
      });
      setEditing(false);
    }
  }, [asset]);

  const handleSave = async () => {
    const tags = formData.tags.split(',').map(t => t.trim()).filter(t => t);
    await onUpdate(asset.id, { ...formData, tags });
    setEditing(false);
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(`${BACKEND_URL}${asset.url}`);
    toast.success('URL copied to clipboard');
  };

  if (!asset) return null;

  const typeConfig = assetTypeIcons[asset.asset_type] || assetTypeIcons.other;
  const TypeIcon = typeConfig.icon;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="dark:bg-slate-800 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-white">
            <div className={`p-2 rounded-lg ${typeConfig.color}`}>
              <TypeIcon className="h-5 w-5" />
            </div>
            {editing ? 'Edit Asset' : 'Asset Details'}
          </DialogTitle>
        </DialogHeader>

        {/* Preview */}
        <div className="mb-4">
          {asset.asset_type === 'image' ? (
            <img 
              src={`${BACKEND_URL}${asset.url}`} 
              alt={asset.name}
              className="max-h-64 rounded-lg mx-auto object-contain bg-slate-100 dark:bg-slate-900"
            />
          ) : (
            <div className={`h-32 rounded-lg flex items-center justify-center ${typeConfig.color}`}>
              <TypeIcon className="h-12 w-12" />
            </div>
          )}
        </div>

        {editing ? (
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="dark:bg-slate-700"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="dark:bg-slate-700"
                rows={2}
              />
            </div>
            <div>
              <Label>Tags (comma-separated)</Label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                className="dark:bg-slate-700"
                placeholder="e.g., marketing, logo, brand"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} className="flex-1">Save</Button>
              <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold dark:text-white text-lg">{asset.name}</h3>
              {asset.description && (
                <p className="text-muted-foreground dark:text-slate-400 mt-1">{asset.description}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <HardDrive className="h-4 w-4" />
                <span>{formatFileSize(asset.file_size)}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <File className="h-4 w-4" />
                <span>{asset.file_extension}</span>
              </div>
              {asset.width && asset.height && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Image className="h-4 w-4" />
                  <span>{asset.width} × {asset.height}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>{new Date(asset.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground col-span-2">
                <User className="h-4 w-4" />
                <span>{asset.created_by_name || 'Unknown'}</span>
              </div>
            </div>

            {asset.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {asset.tags.map((tag, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    <Tag className="h-3 w-3 mr-1" />
                    {tag}
                  </Badge>
                ))}
              </div>
            )}

            <Separator />

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Button variant="outline" onClick={copyUrl}>
                <Copy className="h-4 w-4 mr-2" /> Copy URL
              </Button>
              <Button variant="outline" asChild>
                <a href={`${BACKEND_URL}${asset.url}`} download={asset.filename} target="_blank" rel="noreferrer">
                  <Download className="h-4 w-4 mr-2" /> Download
                </a>
              </Button>
              <Button 
                variant="destructive"
                onClick={() => {
                  onDelete(asset.id);
                  onClose();
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Upload Progress Modal
const UploadProgressModal = ({ open, files, progress }) => {
  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="dark:bg-slate-800 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-white">
            <Upload className="h-5 w-5 text-blue-500" />
            Uploading {files.length} file{files.length > 1 ? 's' : ''}...
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {files.map((file, idx) => (
            <div key={idx} className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="truncate dark:text-white">{file.name}</span>
                <span className="text-muted-foreground">{formatFileSize(file.size)}</span>
              </div>
              <Progress value={progress[idx] || 0} className="h-2" />
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Assets Page
export const Assets = () => {
  const fileInputRef = useRef(null);
  
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  
  // Filters
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Selection
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});

  const fetchAssets = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      let url = `${API}/assets?limit=100`;
      if (filterType !== 'all') url += `&asset_type=${filterType}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const [assetsRes, summaryRes] = await Promise.all([
        axios.get(url, { headers }),
        axios.get(`${API}/assets/summary`, { headers })
      ]);
      
      setAssets(assetsRes.data?.assets || []);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [filterType, searchQuery]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setIsUploading(true);
    setUploadFiles(Array.from(files));
    setUploadProgress({});
    
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    
    let successCount = 0;
    let failCount = 0;
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        await axios.post(`${API}/assets/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [i]: percentCompleted }));
          }
        });
        
        successCount++;
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        failCount++;
      }
    }
    
    setIsUploading(false);
    setUploadFiles([]);
    setUploadProgress({});
    
    if (successCount > 0) {
      toast.success(`Uploaded ${successCount} file${successCount > 1 ? 's' : ''}`);
      fetchAssets();
    }
    if (failCount > 0) {
      toast.error(`Failed to upload ${failCount} file${failCount > 1 ? 's' : ''}`);
    }
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm('Delete this asset?')) return;
    
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.delete(`${API}/assets/${assetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asset deleted');
      fetchAssets();
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };

  const handleUpdate = async (assetId, updates) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.patch(`${API}/assets/${assetId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asset updated');
      fetchAssets();
    } catch (error) {
      toast.error('Failed to update asset');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleUpload(files);
    }
  };

  return (
    <Layout>
      <div 
        className="p-6 space-y-6" 
        data-testid="assets-page"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold dark:text-white flex items-center gap-2">
              <FolderOpen className="h-8 w-8 text-orange-500" />
              Assets Library
            </h1>
            <p className="text-muted-foreground dark:text-slate-400 mt-1">
              Manage your images, documents, and media files
            </p>
          </div>
          <Button
            className="bg-orange-500 hover:bg-orange-600"
            onClick={() => fileInputRef.current?.click()}
            data-testid="upload-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Files
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => handleUpload(e.target.files)}
          />
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="dark:bg-slate-800/60">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold dark:text-white">{summary.total_count}</div>
                <div className="text-sm text-muted-foreground">Total Files</div>
              </CardContent>
            </Card>
            <Card className="dark:bg-slate-800/60">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-blue-500">{summary.images_count}</div>
                <div className="text-sm text-muted-foreground">Images</div>
              </CardContent>
            </Card>
            <Card className="dark:bg-slate-800/60">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-orange-500">{summary.documents_count}</div>
                <div className="text-sm text-muted-foreground">Documents</div>
              </CardContent>
            </Card>
            <Card className="dark:bg-slate-800/60">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-purple-500">{summary.videos_count}</div>
                <div className="text-sm text-muted-foreground">Videos</div>
              </CardContent>
            </Card>
            <Card className="dark:bg-slate-800/60">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-green-500">{summary.audio_count}</div>
                <div className="text-sm text-muted-foreground">Audio</div>
              </CardContent>
            </Card>
            <Card className="dark:bg-slate-800/60">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold dark:text-white">{formatFileSize(summary.total_size_bytes)}</div>
                <div className="text-sm text-muted-foreground">Total Size</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search assets..."
                className="pl-9 dark:bg-slate-800"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-input"
              />
            </div>
          </div>
          
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[150px]" data-testid="filter-type">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="image">Images</SelectItem>
              <SelectItem value="document">Documents</SelectItem>
              <SelectItem value="video">Videos</SelectItem>
              <SelectItem value="audio">Audio</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
          
          <div className="flex border rounded-lg overflow-hidden dark:border-slate-700">
            <Button
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="rounded-none"
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="rounded-none"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Assets Display */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
          </div>
        ) : assets.length === 0 ? (
          <Card className="dark:bg-slate-800/60">
            <CardContent className="p-12 text-center">
              <FolderOpen className="h-16 w-16 mx-auto mb-4 text-slate-400" />
              <h3 className="text-lg font-semibold dark:text-white mb-2">No assets yet</h3>
              <p className="text-muted-foreground mb-4">
                Upload your first file or drag and drop files here
              </p>
              <Button
                className="bg-orange-500 hover:bg-orange-600"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-4 w-4 mr-2" /> Upload Files
              </Button>
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="assets-grid">
            {assets.map(asset => {
              const typeConfig = assetTypeIcons[asset.asset_type] || assetTypeIcons.other;
              const TypeIcon = typeConfig.icon;
              
              return (
                <Card 
                  key={asset.id}
                  className="dark:bg-slate-800/60 cursor-pointer hover:border-orange-500 transition-colors group"
                  onClick={() => setSelectedAsset(asset)}
                  data-testid={`asset-card-${asset.id}`}
                >
                  <CardContent className="p-0">
                    {/* Thumbnail */}
                    <div className="aspect-square bg-slate-100 dark:bg-slate-700 rounded-t flex items-center justify-center overflow-hidden">
                      {asset.asset_type === 'image' ? (
                        <img 
                          src={`${BACKEND_URL}${asset.url}`}
                          alt={asset.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className={`p-6 rounded-lg ${typeConfig.color}`}>
                          <TypeIcon className="h-12 w-12" />
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="p-3">
                      <p className="font-medium dark:text-white truncate text-sm">{asset.name}</p>
                      <div className="flex items-center justify-between mt-1">
                        <Badge variant="secondary" className="text-xs">
                          {asset.asset_type}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {formatFileSize(asset.file_size)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="dark:bg-slate-800/60" data-testid="assets-list">
            <CardContent className="p-0">
              <div className="divide-y dark:divide-slate-700">
                {assets.map(asset => {
                  const typeConfig = assetTypeIcons[asset.asset_type] || assetTypeIcons.other;
                  const TypeIcon = typeConfig.icon;
                  
                  return (
                    <div 
                      key={asset.id}
                      className="flex items-center gap-4 p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer"
                      onClick={() => setSelectedAsset(asset)}
                    >
                      {/* Icon/Thumbnail */}
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden ${typeConfig.color}`}>
                        {asset.asset_type === 'image' ? (
                          <img 
                            src={`${BACKEND_URL}${asset.url}`}
                            alt={asset.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <TypeIcon className="h-6 w-6" />
                        )}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium dark:text-white truncate">{asset.name}</p>
                        <p className="text-sm text-muted-foreground truncate">{asset.filename}</p>
                      </div>
                      
                      {/* Tags */}
                      <div className="hidden md:flex gap-1">
                        {asset.tags?.slice(0, 2).map((tag, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">{tag}</Badge>
                        ))}
                        {asset.tags?.length > 2 && (
                          <Badge variant="secondary" className="text-xs">+{asset.tags.length - 2}</Badge>
                        )}
                      </div>
                      
                      {/* Size & Date */}
                      <div className="text-right text-sm text-muted-foreground hidden sm:block">
                        <div>{formatFileSize(asset.file_size)}</div>
                        <div>{new Date(asset.created_at).toLocaleDateString()}</div>
                      </div>
                      
                      {/* Actions */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(asset.id);
                        }}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Asset Detail Modal */}
        <AssetDetailModal
          asset={selectedAsset}
          open={!!selectedAsset}
          onClose={() => setSelectedAsset(null)}
          onDelete={handleDelete}
          onUpdate={handleUpdate}
        />

        {/* Upload Progress Modal */}
        <UploadProgressModal
          open={isUploading}
          files={uploadFiles}
          progress={uploadProgress}
        />
      </div>
    </Layout>
  );
};
